import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  api,
  type Answer,
  type Attempt,
  type AttemptQuestion,
  type AttemptResult,
  type QuizStatus,
  type UploadedAsset,
} from '../api.ts';
import { Markup } from '../components/Markup.tsx';
import { FileUpload } from '../components/FileUpload.tsx';

/**
 * Sitting a test.
 *
 * The rules that shape this screen:
 *   - an answer is saved the moment it changes, so a closed laptop costs nothing;
 *   - the deadline is the server's, not ours — we count down to `deadline_at` and hand in when it
 *     runs out, rather than trusting a timer that a reload would reset;
 *   - a finished attempt is never editable, so the same component renders the review.
 */

const TYPE_HINTS: Partial<Record<AttemptQuestion['type'], string>> = {
  multi: 'Выберите все подходящие варианты',
  ordering: 'Расставьте в правильном порядке',
  fill: 'Заполните пропуски',
};

/* ---- one question, in exam mode --------------------------------------- */

interface QuestionProps {
  readonly question: AttemptQuestion;
  readonly value: Answer | null;
  readonly onChange: (answer: Answer) => void;
}

function SingleChoice({ question, value, onChange }: QuestionProps): JSX.Element {
  const chosen = value?.type === 'single' ? value.optionId : null;

  return (
    <div className="choices">
      {question.options.map((option) => (
        <label key={option.id} className={`choice${chosen === option.id ? ' choice--on' : ''}`}>
          <input
            type="radio"
            name={question.slot_id}
            checked={chosen === option.id}
            onChange={() => onChange({ type: 'single', optionId: option.id })}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function MultiChoice({ question, value, onChange }: QuestionProps): JSX.Element {
  const chosen = value?.type === 'multi' ? value.optionIds : [];

  function toggle(optionId: string): void {
    const next = chosen.includes(optionId)
      ? chosen.filter((id) => id !== optionId)
      : [...chosen, optionId];
    onChange({ type: 'multi', optionIds: next });
  }

  return (
    <div className="choices">
      {question.options.map((option) => (
        <label
          key={option.id}
          className={`choice${chosen.includes(option.id) ? ' choice--on' : ''}`}
        >
          <input
            type="checkbox"
            checked={chosen.includes(option.id)}
            onChange={() => toggle(option.id)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * Ordering, done with buttons rather than drag and drop.
 *
 * Dragging is nicer with a mouse and unusable with a keyboard or on a phone. Move-up/move-down is
 * neither, and it works everywhere — which matters more for something a grade depends on.
 */
function Ordering({ question, value, onChange }: QuestionProps): JSX.Element {
  const current =
    value?.type === 'ordering' && value.optionIds.length === question.options.length
      ? value.optionIds
      : question.options.map((o) => o.id);

  const labelOf = (id: string): string => question.options.find((o) => o.id === id)?.label ?? '';

  function move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= current.length) return;

    const next = [...current];
    const [moved] = next.splice(index, 1);
    if (moved !== undefined) next.splice(target, 0, moved);
    onChange({ type: 'ordering', optionIds: next });
  }

  return (
    <ol className="ordering">
      {current.map((id, index) => (
        <li key={id}>
          <span className="ordering__pos">{index + 1}</span>
          <span className="ordering__label">{labelOf(id)}</span>
          <span className="ordering__moves">
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={index === 0}
              onClick={() => move(index, -1)}
              aria-label="Выше"
            >
              ↑
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={index === current.length - 1}
              onClick={() => move(index, 1)}
              aria-label="Ниже"
            >
              ↓
            </button>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Blanks({ question, value, onChange }: QuestionProps): JSX.Element {
  const count = question.blank_count ?? 1;
  const values = value?.type === 'fill' ? value.values : [];

  function set(index: number, text: string): void {
    const next = Array.from({ length: count }, (_, i) => (i === index ? text : (values[i] ?? '')));
    onChange({ type: 'fill', values: next });
  }

  return (
    <div className="blanks">
      {Array.from({ length: count }, (_, index) => (
        <label key={index} className="field">
          <span className="muted">Пропуск {index + 1}</span>
          <input
            className="input"
            value={values[index] ?? ''}
            onChange={(e) => set(index, e.target.value)}
          />
        </label>
      ))}
    </div>
  );
}

function FreeText({ question, value, onChange }: QuestionProps): JSX.Element {
  if (question.type === 'number') {
    const current = value?.type === 'number' ? String(value.value) : '';
    return (
      <input
        className="input"
        type="number"
        step="any"
        value={current}
        onChange={(e) => {
          const parsed = Number(e.target.value);
          if (e.target.value !== '' && !Number.isNaN(parsed)) {
            onChange({ type: 'number', value: parsed });
          }
        }}
      />
    );
  }

  const current = value?.type === 'text' ? value.value : '';
  return (
    <input
      className="input"
      value={current}
      onChange={(e) => onChange({ type: 'text', value: e.target.value })}
    />
  );
}

function CodeAnswer({ value, onChange }: QuestionProps): JSX.Element {
  const source = value?.type === 'code' ? value.source : '';
  const language = value?.type === 'code' ? value.language : 'python';

  return (
    <div className="stack">
      <select
        className="input"
        value={language}
        onChange={(e) => onChange({ type: 'code', source, language: e.target.value })}
        style={{ maxWidth: 200 }}
      >
        {['python', 'javascript', 'java', 'cpp', 'csharp'].map((lang) => (
          <option key={lang} value={lang}>
            {lang}
          </option>
        ))}
      </select>
      <textarea
        className="input input--code"
        rows={10}
        value={source}
        onChange={(e) => onChange({ type: 'code', source: e.target.value, language })}
        spellCheck={false}
      />
    </div>
  );
}

function FileAnswer({ value, onChange }: QuestionProps): JSX.Element {
  const [files, setFiles] = useState<UploadedAsset[]>([]);

  useEffect(() => {
    if (value?.type === 'file' && files.length === 0) {
      // Only the id survives a reload; the name is not part of the stored answer.
      setFiles([
        {
          id: value.assetId,
          kind: 'submission',
          original_name: 'Загруженный файл',
          size_bytes: 0,
          mime: '',
          scan_status: 'clean',
          deduplicated: false,
        },
      ]);
    }
  }, [value, files.length]);

  return (
    <FileUpload
      maxFiles={1}
      maxFileMb={50}
      files={files}
      onChange={(next) => {
        setFiles(next);
        const first = next[0];
        if (first) onChange({ type: 'file', assetId: first.id });
      }}
      label="Прикрепить файл"
    />
  );
}

function AnswerWidget(props: QuestionProps): JSX.Element {
  switch (props.question.type) {
    case 'single':
      return <SingleChoice {...props} />;
    case 'multi':
      return <MultiChoice {...props} />;
    case 'ordering':
      return <Ordering {...props} />;
    case 'fill':
      return <Blanks {...props} />;
    case 'code':
      return <CodeAnswer {...props} />;
    case 'file':
      return <FileAnswer {...props} />;
    default:
      return <FreeText {...props} />;
  }
}

/* ---- review ------------------------------------------------------------ */

function answerSummary(question: AttemptQuestion): string {
  const answer = question.answer;
  if (!answer) return 'Без ответа';

  const labelOf = (id: string): string => question.options.find((o) => o.id === id)?.label ?? '—';

  switch (answer.type) {
    case 'single':
      return labelOf(answer.optionId);
    case 'multi':
      return answer.optionIds.map(labelOf).join(', ') || 'Ничего не выбрано';
    case 'ordering':
      return answer.optionIds.map(labelOf).join(' → ');
    case 'fill':
      return answer.values.join(' / ');
    case 'text':
      return answer.value;
    case 'number':
      return String(answer.value);
    case 'file':
      return 'Файл';
    case 'code':
      return answer.source;
  }
}

function ReviewQuestion({
  question,
  index,
}: {
  question: AttemptQuestion;
  index: number;
}): JSX.Element {
  const fraction = question.fraction ?? 0;
  const verdict = question.needs_manual_review
    ? 'wait'
    : fraction >= 1
      ? 'ok'
      : fraction > 0
        ? 'partial'
        : 'bad';

  const correct = new Set(question.correct_option_ids ?? []);

  return (
    <section className={`qcard qcard--${verdict}`}>
      <header className="qcard__head">
        <span className="qcard__index">{index + 1}</span>
        <span className="qcard__mark">
          {question.needs_manual_review
            ? 'Проверит преподаватель'
            : `${Math.round(fraction * 100)}% из ${question.max_mark} б.`}
        </span>
      </header>

      <Markup text={question.text_md} />

      <div className="review">
        <div>
          <span className="muted">Ваш ответ: </span>
          {answerSummary(question)}
        </div>

        {/* The key is only ever shown here, and only when the quiz allows review. */}
        {question.options.length > 0 && question.type !== 'ordering' ? (
          <div>
            <span className="muted">Верно: </span>
            {question.options
              .filter((o) => correct.has(o.id))
              .map((o) => o.label)
              .join(', ')}
          </div>
        ) : null}

        {question.explanation_md ? (
          <div className="notice" style={{ marginTop: 10 }}>
            <Markup text={question.explanation_md} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ---- the countdown ----------------------------------------------------- */

function Countdown({
  deadline,
  onExpire,
}: {
  deadline: string;
  onExpire: () => void;
}): JSX.Element {
  const [left, setLeft] = useState(() => Date.parse(deadline) - Date.now());
  const fired = useRef(false);

  useEffect(() => {
    const tick = setInterval(() => {
      const remaining = Date.parse(deadline) - Date.now();
      setLeft(remaining);

      if (remaining <= 0 && !fired.current) {
        fired.current = true;
        onExpire();
      }
    }, 1000);

    return () => clearInterval(tick);
  }, [deadline, onExpire]);

  const seconds = Math.max(0, Math.floor(left / 1000));
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');

  return (
    <span className={`timer${seconds < 60 ? ' timer--urgent' : ''}`}>
      ⏳ {mm}:{ss}
    </span>
  );
}

/* ---- the screen -------------------------------------------------------- */

export function AttemptPage(): JSX.Element {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [quiz, setQuiz] = useState<QuizStatus | null>(null);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [saving, setSaving] = useState<Record<string, 'saving' | 'saved' | 'failed'>>({});
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!attemptId) return;

    api
      .attempt(attemptId)
      .then(async (loaded) => {
        setAttempt(loaded);
        setAnswers(
          Object.fromEntries(
            loaded.questions
              .filter((q): q is AttemptQuestion & { answer: Answer } => q.answer !== null)
              .map((q) => [q.slot_id, q.answer]),
          ),
        );
        setQuiz(await api.quizStatus(loaded.quiz_id).catch(() => null));
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось открыть попытку'),
      );
  }, [attemptId]);

  // Pending saves must not outlive the screen, or they fire against an unmounted component.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of Object.values(pending)) clearTimeout(timer);
    };
  }, []);

  /**
   * Records an answer locally at once and on the server shortly after.
   *
   * The delay is per question, so typing into a text box does not fire a request per keystroke,
   * while ticking a radio button still lands within a second.
   */
  const record = useCallback(
    (slotId: string, answer: Answer): void => {
      setAnswers((current) => ({ ...current, [slotId]: answer }));
      setSaving((current) => ({ ...current, [slotId]: 'saving' }));

      clearTimeout(timers.current[slotId]);
      timers.current[slotId] = setTimeout(() => {
        if (!attemptId) return;
        api
          .answer(attemptId, slotId, answer)
          .then(() => setSaving((current) => ({ ...current, [slotId]: 'saved' })))
          .catch(() => setSaving((current) => ({ ...current, [slotId]: 'failed' })));
      }, 700);
    },
    [attemptId],
  );

  const hand = useCallback(
    async (auto: boolean): Promise<void> => {
      if (!attemptId || busy) return;
      setBusy(true);
      setError('');

      // Anything still waiting on its debounce would be lost; flush it before finishing.
      for (const [slotId, timer] of Object.entries(timers.current)) {
        clearTimeout(timer);
        const answer = answers[slotId];
        if (answer) await api.answer(attemptId, slotId, answer).catch(() => undefined);
      }

      try {
        setResult(await api.submitAttempt(attemptId));
        setAttempt(await api.attempt(attemptId));
      } catch (cause) {
        setError(
          cause instanceof Error
            ? cause.message
            : auto
              ? 'Время вышло, но отправить не удалось'
              : 'Не удалось отправить',
        );
      } finally {
        setBusy(false);
      }
    },
    [attemptId, answers, busy],
  );

  const expire = useCallback(() => void hand(true), [hand]);

  if (error && !attempt) {
    return (
      <div style={{ paddingTop: 40 }} className="stack">
        <div className="notice notice--accent">{error}</div>
        <Link to="/" className="back">
          ← Мои курсы
        </Link>
      </div>
    );
  }

  if (!attempt) {
    return (
      <p className="muted" style={{ paddingTop: 40 }}>
        Загружаем…
      </p>
    );
  }

  const open = attempt.state === 'in_progress';
  const unanswered = attempt.questions.filter((q) => !answers[q.slot_id]).length;

  /* Finished: the score, then the review if the quiz allows one. */
  if (!open) {
    const fraction = result?.fraction ?? attempt.fraction ?? 0;
    const passed = result?.passed ?? attempt.passed ?? false;

    return (
      <>
        <div className="page-head">
          <h1>{quiz?.title ?? 'Тест'}</h1>
          <p className="muted">Попытка №{attempt.attempt_number}</p>
        </div>

        <section className={`scorecard${passed ? ' scorecard--pass' : ''}`}>
          <div className="scorecard__value">{Math.round(fraction * 100)}%</div>
          <div className="stack">
            <strong>{passed ? 'Тест пройден' : 'Тест не пройден'}</strong>
            {quiz ? (
              <span className="muted">
                Проходной балл — {Math.round(quiz.pass_fraction * 100)}%
              </span>
            ) : null}
            {result?.needs_manual_review ? (
              <span className="muted">
                Часть ответов проверит преподаватель — итог может измениться.
              </span>
            ) : null}
          </div>
        </section>

        <div className="row" style={{ margin: '22px 0' }}>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigate(-1);
            }}
          >
            Вернуться к уроку
          </button>
          {quiz && !passed && quiz.attempts_left !== 0 ? (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                api
                  .startAttempt(attempt.quiz_id)
                  .then((next) => navigate(`/attempts/${next.id}`))
                  .catch((cause: unknown) =>
                    setError(cause instanceof Error ? cause.message : 'Не удалось начать заново'),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              Пересдать
            </button>
          ) : null}
        </div>

        {error ? <p className="error">{error}</p> : null}

        {attempt.review_available ? (
          <>
            <h2 style={{ marginBottom: 16 }}>Разбор</h2>
            {attempt.questions.map((question, index) => (
              <ReviewQuestion key={question.slot_id} question={question} index={index} />
            ))}
          </>
        ) : (
          <div className="notice">
            Разбор ответов для этого теста закрыт — так преподаватель бережёт вопросы для пересдач.
          </div>
        )}
      </>
    );
  }

  /* Open: the paper itself. */
  return (
    <>
      <div className="page-head">
        <h1>{quiz?.title ?? 'Тест'}</h1>
        <div className="row">
          <span className="muted">
            Попытка №{attempt.attempt_number} · вопросов: {attempt.questions.length}
          </span>
          {attempt.deadline_at ? (
            <Countdown deadline={attempt.deadline_at} onExpire={expire} />
          ) : null}
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}

      {attempt.questions.map((question, index) => {
        const state = saving[question.slot_id];

        return (
          <section key={question.slot_id} className="qcard">
            <header className="qcard__head">
              <span className="qcard__index">{index + 1}</span>
              <span className="muted">{question.max_mark} б.</span>
              <span className={`saveflag saveflag--${state ?? 'idle'}`}>
                {state === 'saving'
                  ? 'сохраняем…'
                  : state === 'saved'
                    ? 'сохранено'
                    : state === 'failed'
                      ? 'не сохранилось'
                      : ''}
              </span>
            </header>

            <Markup text={question.text_md} />
            {TYPE_HINTS[question.type] ? (
              <p className="muted" style={{ fontSize: '0.84rem', marginTop: -4 }}>
                {TYPE_HINTS[question.type]}
              </p>
            ) : null}

            <AnswerWidget
              question={question}
              value={answers[question.slot_id] ?? null}
              onChange={(answer) => record(question.slot_id, answer)}
            />
          </section>
        );
      })}

      <div className="submitbar">
        <span className="muted">
          {unanswered === 0
            ? 'Отвечены все вопросы'
            : `Без ответа: ${unanswered} из ${attempt.questions.length}`}
        </span>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => {
            if (
              unanswered > 0 &&
              !window.confirm(
                `Без ответа ещё ${unanswered}. Они будут засчитаны как неверные. Всё равно сдать?`,
              )
            ) {
              return;
            }
            void hand(false);
          }}
        >
          {busy ? 'Отправляем…' : 'Сдать тест'}
        </button>
      </div>
    </>
  );
}
